import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit2, Trash2, FileText, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import DataTable from '../../../components/common/DataTable';
import CustomerForm from './CustomerForm';
import customerService from '../../../services/customerService';

const CustomerList = () => {
  const [view, setView] = useState('list'); // 'list', 'add', 'edit', 'detail'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'customerCode', direction: 'ASC' });
  const [searchValue, setSearchValue] = useState('');

  // Fetch customers on mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Fetch all customers
  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const data = await customerService.getAllCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      // Error already handled by axios interceptor with toast
    } finally {
      setIsLoading(false);
    }
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer =>
    customer.customerName.toLowerCase().includes(searchValue.toLowerCase()) ||
    customer.customerCode.toLowerCase().includes(searchValue.toLowerCase()) ||
    (customer.pics && customer.pics.some(pic => 
      pic.nama.toLowerCase().includes(searchValue.toLowerCase()) ||
      (pic.email && pic.email.toLowerCase().includes(searchValue.toLowerCase()))
    ))
  );

  // Sort customers
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const aValue = a[sortConfig.sortBy];
    const bValue = b[sortConfig.sortBy];
    
    if (aValue < bValue) return sortConfig.direction === 'ASC' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ASC' ? 1 : -1;
    return 0;
  });

  // Paginate customers
  const startIndex = currentPage * pageSize;
  const paginatedCustomers = sortedCustomers.slice(startIndex, startIndex + pageSize);

  // Define columns for DataTable
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {startIndex + index + 1}
        </span>
      )
    },
    {
      key: 'customerCode',
      label: 'Code',
      sortable: true,
      render: (row) => (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
          {row.customerCode}
        </span>
      )
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.customerName}</div>
          <div className="text-xs text-gray-500 mt-1">
            {row.pics && row.pics.length > 0 ? (
              <span className="inline-flex items-center gap-1">
                <span className="font-medium">{row.pics.length}</span> 
                PIC{row.pics.length > 1 ? 's' : ''}
                {row.pics.length > 0 && (
                  <span className="text-gray-400">• {row.pics[0].nama}</span>
                )}
              </span>
            ) : (
              <span className="text-amber-600">No PIC</span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-gray-700">{row.type}</span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleView(row)}
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleEdit(row)}
            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  // Handlers
  const handleAddNew = () => {
    setView('add');
    setSelectedCustomer(null);
  };

  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setView('detail');
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setView('edit');
  };

  const handleDelete = async (customer) => {
    if (window.confirm(`Are you sure you want to delete ${customer.customerName}?`)) {
      try {
        await customerService.softDeleteCustomer(customer.id);
        toast.success('Customer deleted successfully!');
        fetchCustomers(); // Refresh list
      } catch (error) {
        console.error('Error deleting customer:', error);
        // Error already handled by axios interceptor
      }
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedCustomer(null);
    fetchCustomers(); // Refresh list when coming back
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  // Render Form View
  if (view === 'add' || view === 'edit') {
    return (
      <CustomerForm
        onBack={handleBackToList}
        customerData={selectedCustomer}
        mode={view}
      />
    );
  }

  // Render Detail View
  if (view === 'detail' && selectedCustomer) {
    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToList}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Customer Details</h1>
                <p className="text-gray-600 mt-1">View customer information</p>
              </div>
            </div>
            <button
              onClick={() => handleEdit(selectedCustomer)}
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Edit2 className="w-5 h-5" />
              Edit Customer
            </button>
          </div>
        </div>

        {/* Detail Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Customer Code</label>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                  {selectedCustomer.customerCode}
                </span>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Type</label>
                <p className="text-base text-gray-900">{selectedCustomer.type}</p>
              </div>
              <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Customer Name</label>
                <p className="text-lg font-medium text-gray-900">{selectedCustomer.customerName}</p>
              </div>
              <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Address</label>
                <p className="text-base text-gray-900">{selectedCustomer.customerAddress}</p>
              </div>
            </div>
          </div>

          {/* PIC Section */}
          <div className="border-t border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Person In Charge (PIC)</h2>
            {selectedCustomer.pics && selectedCustomer.pics.length > 0 ? (
              <div className="space-y-4">
                {selectedCustomer.pics.map((pic) => (
                  <div key={pic.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Nama</label>
                        <p className="text-sm font-medium text-gray-900">{pic.nama}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
                        <p className="text-sm text-gray-700">{pic.email || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Departemen</label>
                        <p className="text-sm text-gray-700">{pic.departemen || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Jabatan</label>
                        <p className="text-sm text-gray-700">{pic.jabatan || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Phone</label>
                        <p className="text-sm text-gray-700">{pic.phone}</p>
                      </div>
                      {pic.remarks && (
                        <div className="md:col-span-2">
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Remarks</label>
                          <p className="text-sm text-gray-700">{pic.remarks}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No PIC available for this customer</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render List View (Default) - Using DataTable Component
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">     
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Customer List</h1>
              <p className="text-gray-600 mt-1">Manage and view all customers</p>
            </div>
          </div>
          <button 
            onClick={handleAddNew}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Customer
          </button>
        </div>
      </div>

      {/* DataTable Component */}
      <DataTable
        columns={columns}
        data={paginatedCustomers}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={filteredCustomers.length}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortConfig={sortConfig}
        isLoading={isLoading}
        onSearch={handleSearch}
        searchValue={searchValue}
        searchPlaceholder="Search by customer name, code, or PIC..."
        enableQuickJump={true}
        customPageSizes={[5, 10, 25, 50]}
        striped={false}
        headerBgColor="bg-gradient-to-r from-red-600 to-red-500"
        headerTextColor="text-white"
        hoverColor="hover:bg-red-50"
      />
    </div>
  );
};

export default CustomerList;